/**
 * Feature Toggle Service
 * 
 * Allows enabling/disabling features in development and non-production environments.
 * In production, toggles are controlled by environment variables only.
 * In development, toggles can be overridden via localStorage for testing.
 */

 type FeatureToggleKey = 
  | 'rootDomainRegistration' // Enable registration page on root domain
  | 'multiTenancy' // Enable multi-tenant routing
  | 'appDevRoutes' // Enable /app/* routes in development
  | 'allergiesMockService' // Use in-memory allergy service instead of HTTP backend

// @ts-ignore - Type is defined for future use
type FeatureToggleConfig = Record<string, boolean | undefined>

// Helper function to check if we're in development (needed for default toggles)
function isDevelopmentForDefaults(): boolean {
  try {
    if (typeof window !== 'undefined') {
      return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    }
  } catch {
    // Ignore errors
  }
  return false
}

// Default feature toggle values
 const DEFAULT_TOGGLES: Record<FeatureToggleKey, boolean> = {
  rootDomainRegistration: true,
  multiTenancy: true,
  appDevRoutes: true,
  // Default to true in dev (will be overridden by env var or localStorage)
  // In production, should be set to false via environment variable
  allergiesMockService: isDevelopmentForDefaults(),
 }

// Helper function to safely access import.meta.env
function getEnvVar(key: string): string {
  try {
    // @ts-ignore - import.meta may not be available in Jest
    if (typeof globalThis.import !== 'undefined' && globalThis.import.meta?.env) {
      // @ts-ignore
      return globalThis.import.meta.env[key] || ''
    }
  } catch {
    // Ignore errors
  }
  
  // Fallback to process.env for Jest/Node environments
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || ''
  }
  
  return ''
}

// Helper function to check if we're in development
function isDevelopment(): boolean {
  try {
    // @ts-ignore
    if (typeof globalThis.import !== 'undefined' && globalThis.import.meta?.env) {
      // @ts-ignore
      return globalThis.import.meta.env.DEV === true || 
             (typeof window !== 'undefined' && window.location.hostname === 'localhost')
    }
  } catch {
    // Ignore errors
  }
  
  return typeof window !== 'undefined' && window.location.hostname === 'localhost'
}

// Get environment-based feature toggles
function getEnvToggles(): Partial<Record<FeatureToggleKey, boolean>> {
  const toggles: Partial<Record<FeatureToggleKey, boolean>> = {}
  
  try {
    // Root domain registration
    const rootDomainReg = getEnvVar('VITE_FEATURE_ROOT_DOMAIN_REGISTRATION')
    if (rootDomainReg !== '') {
      toggles.rootDomainRegistration = rootDomainReg === 'true'
    }
    
    // Multi-tenancy
    const multiTenancy = getEnvVar('VITE_FEATURE_MULTI_TENANCY')
    if (multiTenancy !== '') {
      toggles.multiTenancy = multiTenancy === 'true'
    }
    
    // App dev routes
    const appDevRoutes = getEnvVar('VITE_FEATURE_APP_DEV_ROUTES')
    if (appDevRoutes !== '') {
      toggles.appDevRoutes = appDevRoutes === 'true'
    }

    // Allergies mock service
    const allergiesMockService = getEnvVar('VITE_FEATURE_ALLERGIES_MOCK_SERVICE')
    if (allergiesMockService !== '') {
      toggles.allergiesMockService = allergiesMockService === 'true'
    }
  } catch (error) {
    console.warn('[FeatureToggles] Error reading environment variables:', error)
  }
  
  return toggles
}

// Get localStorage overrides (development only)
function getLocalStorageToggles(): Partial<Record<FeatureToggleKey, boolean>> {
  if (typeof window === 'undefined') {
    return {}
  }
  
  if (!isDevelopment()) {
    return {} // Don't allow localStorage overrides in production
  }
  
  try {
    const stored = localStorage.getItem('clnicl_feature_toggles')
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.warn('[FeatureToggles] Error reading localStorage:', error)
  }
  
  return {}
}

// Get current feature toggle value
function getToggle(key: FeatureToggleKey): boolean {
  // Priority: localStorage (dev only) > environment > default
  const envToggles = getEnvToggles()
  const localStorageToggles = getLocalStorageToggles()
  
  // Check localStorage first (dev only)
  if (localStorageToggles[key] !== undefined) {
    return localStorageToggles[key]!
  }
  
  // Check environment variables
  if (envToggles[key] !== undefined) {
    return envToggles[key]!
  }
  
  // Return default
  return DEFAULT_TOGGLES[key]
}

// Set feature toggle (development only)
function setToggle(key: FeatureToggleKey, value: boolean): void {
  if (typeof window === 'undefined') {
    return
  }
  
  if (!isDevelopment()) {
    console.warn('[FeatureToggles] Cannot set toggles in production')
    return
  }
  
  try {
    const current = getLocalStorageToggles()
    const updated = { ...current, [key]: value }
    localStorage.setItem('clnicl_feature_toggles', JSON.stringify(updated))
    console.log(`[FeatureToggles] Set ${key} = ${value}`)
    
    // Reload page to apply changes
    window.location.reload()
  } catch (error) {
    console.error('[FeatureToggles] Error setting toggle:', error)
  }
}

// Get all feature toggles
function getAllToggles(): Record<FeatureToggleKey, boolean> {
  const toggles: Partial<Record<FeatureToggleKey, boolean>> = {}
  
  for (const key of Object.keys(DEFAULT_TOGGLES) as FeatureToggleKey[]) {
    toggles[key] = getToggle(key)
  }
  
  return toggles as Record<FeatureToggleKey, boolean>
}

// Reset all toggles to defaults
function resetToggles(): void {
  if (typeof window === 'undefined') {
    return
  }
  
  if (!isDevelopment()) {
    return
  }
  
  try {
    localStorage.removeItem('clnicl_feature_toggles')
    console.log('[FeatureToggles] Reset all toggles to defaults')
    window.location.reload()
  } catch (error) {
    console.error('[FeatureToggles] Error resetting toggles:', error)
  }
}

// Expose toggles on window for easy debugging in dev
if (typeof window !== 'undefined') {
  if (isDevelopment()) {
    (window as any).featureToggles = {
      get: getToggle,
      set: setToggle,
      getAll: getAllToggles,
      reset: resetToggles,
    }
    console.log('[FeatureToggles] Available in console: window.featureToggles')
  }
}

export const featureToggles = {
  get: getToggle,
  set: setToggle,
  getAll: getAllToggles,
  reset: resetToggles,
}
